import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNotification } from '../../contexts/NotificationContext';
import PensionDAConfig from './PensionDAConfig';
import PensionPayslipDetail from './PensionPayslipDetail';
import { Landmark, Calendar, Play, CheckCircle, Clock, AlertCircle, FileText, ChevronRight, Eye, Check, Download, Upload, Edit2, X, Save, Trash2 } from 'lucide-react';
import { buildApiUrl } from '../../utils/apiSetup';

const PensionPayrollManagement = () => {
  const { hasPermission, user } = useAuth();

  const isAdmin = ['super_administrator', 'admin', 'hr_manager', 'hospital_owner'].includes(user?.role_name?.toLowerCase());

  // Permissions
  const canViewGenTab = hasPermission?.('pension_payroll:generation:view') || hasPermission?.('*');
  const canViewHistTab = hasPermission?.('pension_payroll:historical:view') || hasPermission?.('*');
  const canViewMasterTab = hasPermission?.('pension_payroll:master:view') || hasPermission?.('*');

  const canGenerate = hasPermission?.('pension_payroll:generate') || hasPermission?.('*');
  const canApprove = hasPermission?.('pension_payroll:approve') || hasPermission?.('*');
  const canHistory = hasPermission?.('pension_payroll:history') || hasPermission?.('*');
  const canDetail = hasPermission?.('pension_payroll:detail') || hasPermission?.('*');
  const canManageDA = hasPermission?.('pension_payroll:master:manage') || hasPermission?.('*');
  const canExport = hasPermission?.('pension_payroll:export') || hasPermission?.('*');

  const defaultTab = canViewGenTab ? 'generation' : (canViewHistTab ? 'historical' : (canViewMasterTab ? 'master' : ''));

  // Three ribbon tabs: 'generation', 'historical', 'master'
  const [activeRibbonTab, setActiveRibbonTab] = useState(defaultTab);

  const [runs, setRuns] = useState([]);
  const [selectedRun, setSelectedRun] = useState(null);
  const [records, setRecords] = useState([]);
  const [pensioners, setPensioners] = useState([]);
  const [selectedPensionerIds, setSelectedPensionerIds] = useState([]);

  const { showSuccess, showError } = useNotification();
  const [loading, setLoading] = useState(false);
  const setError = (msg) => { if (msg) showError(msg); };
  const setSuccess = (msg) => { if (msg) showSuccess(msg); };

  // PDF Preview & Download States
  const [showPdfModal, setShowPdfModal] = useState(false);
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);

  // Pension Paybill Summary Modal States
  const [summaryModalOpen, setSummaryModalOpen] = useState(false);
  const [summaryData, setSummaryData] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [selectedSummaryRun, setSelectedSummaryRun] = useState(null);

  // Row Editing States (Pensioner Master or Draft record edits)
  const [editingEmployeeId, setEditingEmployeeId] = useState(null);
  const [editingRecordId, setEditingRecordId] = useState(null); // specific to draft record
  const [editBasic, setEditBasic] = useState('');
  const [editDaPct, setEditDaPct] = useState('');
  const [editMedical, setEditMedical] = useState('');
  const [editDaArrear, setEditDaArrear] = useState('');
  const [editCommutation, setEditCommutation] = useState('');
  const [editRecovery, setEditRecovery] = useState('');
  const [editIncomeTax, setEditIncomeTax] = useState('');

  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();
  const currentMonth = currentDate.getMonth() + 1; // 1-based (e.g. Sept = 9)

  // Generation state (Start 2025 up to currentYear, months up to currentMonth)
  const genYearOptions = React.useMemo(() => {
    const startYr = 2025;
    const endYr = Math.max(2025, currentYear);
    const yrs = [];
    for (let y = startYr; y <= endYr; y++) {
      yrs.push(y.toString());
    }
    return yrs;
  }, [currentYear]);

  const [genYear, setGenYear] = useState(() => Math.max(2025, currentYear).toString());

  const genMonthOptions = React.useMemo(() => {
    const yr = parseInt(genYear);
    const minM = (yr === 2025) ? 4 : 1;
    const maxM = (yr === currentYear) ? currentMonth : 12;
    const mList = [];
    for (let m = minM; m <= maxM; m++) {
      mList.push(m);
    }
    return mList;
  }, [genYear, currentYear, currentMonth]);

  const [genMonth, setGenMonth] = useState(() => {
    const yr = currentYear;
    const minM = (yr === 2025) ? 4 : 1;
    const maxM = (yr === currentYear) ? currentMonth : 12;
    const targetM = Math.min(currentMonth, maxM);
    return Math.max(minM, targetM).toString();
  });

  const [genDescription, setGenDescription] = useState('');
  const [generating, setGenerating] = useState(false);

  // Historical Import state (Years 2025 up to currentYear, 2025 starts at April 2025)
  const histYearOptions = React.useMemo(() => {
    const startYr = 2025;
    const endYr = Math.max(2025, currentYear);
    const yrs = [];
    for (let y = startYr; y <= endYr; y++) {
      yrs.push(y.toString());
    }
    return yrs;
  }, [currentYear]);

  const [histYear, setHistYear] = useState('2025');

  const histMonthOptions = React.useMemo(() => {
    const yr = parseInt(histYear);
    const minM = (yr === 2025) ? 4 : 1;
    const maxM = (yr === currentYear) ? currentMonth : 12;
    const mList = [];
    for (let m = minM; m <= maxM; m++) {
      mList.push(m);
    }
    return mList;
  }, [histYear, currentYear, currentMonth]);

  const [histMonth, setHistMonth] = useState('4');
  const [histFile, setHistFile] = useState(null);
  const [importing, setImporting] = useState(false);

  const handleHistYearChange = (e) => {
    const selectedYr = e.target.value;
    setHistYear(selectedYr);
    if (selectedYr === '2025') {
      setHistMonth('4');
    } else {
      setHistMonth('1');
    }
  };

  const handleGenYearChange = (e) => {
    const selectedYr = e.target.value;
    setGenYear(selectedYr);
    const yrInt = parseInt(selectedYr);
    const minM = (yrInt === 2025) ? 4 : 1;
    const maxM = (yrInt === currentYear) ? currentMonth : 12;
    let currentGenM = parseInt(genMonth);
    if (currentGenM < minM) {
      setGenMonth(minM.toString());
    } else if (currentGenM > maxM) {
      setGenMonth(maxM.toString());
    }
  };

  // Draft import update state
  const [draftUpdateFile, setDraftUpdateFile] = useState(null);
  const [updatingDraft, setUpdatingDraft] = useState(false);

  // Global apply state
  const [globalDa, setGlobalDa] = useState('');
  const [globalMedical, setGlobalMedical] = useState('');
  const [globalMode, setGlobalMode] = useState('all'); // 'all' or 'selected'

  // Payslip detail state
  const [viewPayslipEmployeeId, setViewPayslipEmployeeId] = useState(null);

  useEffect(() => {
    if (canViewGenTab) {
      fetchRuns();
    }
    if (canViewMasterTab) {
      fetchPensioners();
    }
  }, [canViewGenTab, canViewMasterTab]);

  const fetchRuns = async () => {
    try {
      setLoading(true);
      const response = await fetch(buildApiUrl('/api/v1/pension-payroll/runs'), {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setRuns(data);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchPensioners = async () => {
    try {
      const response = await fetch(buildApiUrl('/api/v1/pension-payroll/pensioner-master'), {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setPensioners(data);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleGenerate = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setGenerating(true);
    try {
      const response = await fetch(buildApiUrl('/api/v1/pension-payroll/generate'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        },
        body: JSON.stringify({
          payroll_month: parseInt(genMonth),
          payroll_year: parseInt(genYear),
          description: genDescription
        })
      });
      const data = await response.json();
      if (response.ok) {
        setSuccess('Pension payroll run generated successfully in draft.');
        setGenDescription('');
        fetchRuns();
        handleSelectRun(data.id);
        setActiveRibbonTab('generation');
      } else {
        setError(data.detail || 'Failed to generate pension payroll.');
      }
    } catch (err) {
      setError('Network error occurred.');
    } finally {
      setGenerating(false);
    }
  };

  const handleSelectRun = async (runId) => {
    setError('');
    setSuccess('');
    setViewPayslipEmployeeId(null);
    setEditingRecordId(null);
    try {
      const response = await fetch(buildApiUrl(`/api/v1/pension-payroll/runs/${runId}`), {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setSelectedRun(data.run);
        setRecords(data.records);
      }
    } catch (err) {
      console.error(err);
      setError('Failed to fetch run details.');
    }
  };

  const handleApproveRun = async (runId) => {
    setError('');
    setSuccess('');
    try {
      const response = await fetch(buildApiUrl(`/api/v1/pension-payroll/runs/${runId}/approve`), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });
      if (response.ok) {
        setSuccess('Pension payroll run approved and finalized.');
        fetchRuns();
        handleSelectRun(runId);
      } else {
        const data = await response.json();
        setError(data.detail || 'Failed to approve run.');
      }
    } catch (err) {
      setError('Network error occurred.');
    }
  };

  const handleRejectRun = async (runId) => {
    if (!window.confirm("Are you sure you want to reject this approved pension payroll run? This will revert it back to draft status.")) {
      return;
    }
    setError('');
    setSuccess('');
    try {
      const response = await fetch(buildApiUrl(`/api/v1/pension-payroll/runs/${runId}/reject`), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });
      if (response.ok) {
        setSuccess('Pension payroll run rejected and reverted to draft.');
        fetchRuns();
        handleSelectRun(runId);
      } else {
        const data = await response.json();
        setError(data.detail || 'Failed to reject run.');
      }
    } catch (err) {
      setError('Network error occurred.');
    }
  };

  const handleDeleteRun = async (runId) => {
    if (!window.confirm("Are you sure you want to delete this draft pension run?")) return;
    setError('');
    setSuccess('');
    try {
      const response = await fetch(buildApiUrl(`/api/v1/pension-payroll/runs/${runId}`), {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });
      if (response.ok) {
        setSuccess('Draft pension payroll run deleted successfully.');
        setSelectedRun(null);
        fetchRuns();
      } else {
        const data = await response.json();
        setError(data.detail || 'Failed to delete draft run.');
      }
    } catch (err) {
      setError('Network error occurred.');
    }
  };

  const handleExportRun = async (runId) => {
    try {
      const response = await fetch(buildApiUrl(`/api/v1/pension-payroll/runs/${runId}/export/excel`), {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `pension_payroll_run_${runId}.xlsx`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handlePreviewPdf = async (runId) => {
    try {
      setPdfLoading(true);
      setError('');
      const response = await fetch(buildApiUrl(`/api/v1/pension-payroll/runs/${runId}/export/pdf`), {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });
      if (response.ok) {
        const blob = await response.blob();
        const blobUrl = URL.createObjectURL(blob);
        setPdfPreviewUrl(blobUrl);
        setShowPdfModal(true);
      } else {
        setError('Failed to generate PDF preview.');
      }
    } catch (err) {
      console.error(err);
      setError('Network error generating PDF preview.');
    } finally {
      setPdfLoading(false);
    }
  };

  const handleDownloadPdf = async (run) => {
    try {
      setError('');
      const response = await fetch(buildApiUrl(`/api/v1/pension-payroll/runs/${run.id}/export/pdf`), {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        const monthNames = ["", "January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
        const mName = monthNames[run.payroll_month] || run.payroll_month;
        a.download = `Pension_Payroll_${mName}_${run.payroll_year}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      } else {
        setError('Failed to download PDF.');
      }
    } catch (err) {
      console.error(err);
      setError('Network error downloading PDF.');
    }
  };

  const handleOpenSummaryReport = async (run) => {
    setSelectedSummaryRun(run);
    setSummaryLoading(true);
    setSummaryModalOpen(true);
    setSummaryData(null);
    try {
      const response = await fetch(buildApiUrl(`/api/v1/pension-payroll/runs/${run.id}/summary`), {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setSummaryData(data);
      } else {
        setError('Failed to fetch pension paybill summary.');
      }
    } catch (err) {
      console.error(err);
      setError('Network error fetching summary report.');
    } finally {
      setSummaryLoading(false);
    }
  };

  const handleDownloadSummaryPdf = async (run) => {
    try {
      const response = await fetch(buildApiUrl(`/api/v1/pension-payroll/runs/${run.id}/export/summary-pdf`), {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        const monthNames = ["", "January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
        const mName = monthNames[run.payroll_month] || run.payroll_month;
        a.download = `Pension_Paybill_Summary_${mName}_${run.payroll_year}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      } else {
        setError('Failed to download summary PDF.');
      }
    } catch (err) {
      console.error(err);
      setError('Network error downloading summary PDF.');
    }
  };

  // Row edit handlers for Master
  const startEditMaster = (p) => {
    setEditingEmployeeId(p.employee_id);
    setEditBasic(p.basic_pension);
    setEditDaPct(p.da_percentage);
    setEditMedical(p.medical_allowance);
  };

  const saveEditMaster = async (employeeId) => {
    setError('');
    setSuccess('');
    try {
      const response = await fetch(buildApiUrl(`/api/v1/pension-payroll/pensioner-master/${employeeId}`), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        },
        body: JSON.stringify({
          basic_pension: parseFloat(editBasic),
          da_percentage_override: parseFloat(editDaPct),
          medical_allowance: parseFloat(editMedical)
        })
      });
      if (response.ok) {
        setSuccess('Pensioner master record updated successfully.');
        setEditingEmployeeId(null);
        fetchPensioners();
      } else {
        const data = await response.json();
        setError(data.detail || 'Failed to save edit.');
      }
    } catch (err) {
      setError('Network error occurred.');
    }
  };

  // Row edit handlers for Draft Record
  const startEditDraftRecord = (record) => {
    setEditingRecordId(record.id);
    setEditBasic(record.basic_pension);
    setEditDaPct(record.da_percentage);
    setEditMedical(record.medical_allowance);
    setEditDaArrear(record.da_arrear || 0);
    setEditCommutation(record.commutation_amount || 0);
    setEditRecovery(record.recovery || 0);
    setEditIncomeTax(record.income_tax || 0);
  };

  const saveEditDraftRecord = async (runId, recordId) => {
    setError('');
    try {
      const response = await fetch(buildApiUrl(`/api/v1/pension-payroll/runs/${runId}/records/${recordId}`), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        },
        body: JSON.stringify({
          basic_pension: parseFloat(editBasic),
          da_percentage: parseFloat(editDaPct),
          medical_allowance: parseFloat(editMedical),
          da_arrear: parseFloat(editDaArrear),
          commutation_amount: parseFloat(editCommutation),
          recovery: parseFloat(editRecovery),
          income_tax: parseFloat(editIncomeTax)
        })
      });
      if (response.ok) {
        setSuccess('Draft record updated successfully.');
        setEditingRecordId(null);
        handleSelectRun(runId);
      } else {
        const data = await response.json();
        setError(data.detail || 'Failed to save edit.');
      }
    } catch (err) {
      setError('Network error occurred.');
    }
  };

  // Global Assign handler
  const handleApplyGlobal = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    if (!globalDa && !globalMedical) {
      setError('Please provide either DA % or Medical Allowance.');
      return;
    }
    try {
      const response = await fetch(buildApiUrl('/api/v1/pension-payroll/da-config/apply-global'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        },
        body: JSON.stringify({
          da_percentage: globalDa ? parseFloat(globalDa) : null,
          medical_allowance: globalMedical ? parseFloat(globalMedical) : null,
          mode: globalMode,
          employee_ids: globalMode === 'selected' ? selectedPensionerIds : []
        })
      });
      const data = await response.json();
      if (response.ok) {
        setSuccess(data.message || 'Global configuration applied successfully.');
        setGlobalDa('');
        setGlobalMedical('');
        fetchPensioners();
        setSelectedPensionerIds([]);
      } else {
        setError(data.detail || 'Failed to apply global settings.');
      }
    } catch (err) {
      setError('Network error occurred.');
    }
  };

  // Historical Import handler
  const handleHistoricalImport = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    if (!histFile) {
      setError('Please select a spreadsheet file to upload.');
      return;
    }
    setImporting(true);
    try {
      const formData = new FormData();
      formData.append('month', histMonth);
      formData.append('year', histYear);
      formData.append('file', histFile);

      const response = await fetch(buildApiUrl('/api/v1/pension-payroll/historical/import'), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        },
        body: formData
      });
      const data = await response.json();
      if (response.ok) {
        setSuccess(`Historical pension payroll imported. Registered run ID: ${data.run_id}`);
        setHistFile(null);
        fetchRuns();
      } else {
        setError(data.detail || 'Failed to import historical payroll.');
      }
    } catch (err) {
      setError('Network error occurred.');
    } finally {
      setImporting(false);
    }
  };

  // Draft Update Import handler
  const handleImportDraftUpdate = async (e) => {
    e.preventDefault();
    if (!draftUpdateFile) return;
    setUpdatingDraft(true);
    setError('');
    setSuccess('');
    try {
      const formData = new FormData();
      formData.append('file', draftUpdateFile);

      const response = await fetch(buildApiUrl(`/api/v1/pension-payroll/runs/${selectedRun.id}/import-update`), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        },
        body: formData
      });
      const data = await response.json();
      if (response.ok) {
        setSuccess(`Draft updated successfully. Updated ${data.updated_records} records.`);
        setDraftUpdateFile(null);
        handleSelectRun(selectedRun.id);
      } else {
        setError(data.detail || 'Failed to update draft run from spreadsheet.');
      }
    } catch (err) {
      setError('Network error occurred.');
    } finally {
      setUpdatingDraft(false);
    }
  };

  const handleCheckboxChange = (employeeId) => {
    if (selectedPensionerIds.includes(employeeId)) {
      setSelectedPensionerIds(selectedPensionerIds.filter(id => id !== employeeId));
    } else {
      setSelectedPensionerIds([...selectedPensionerIds, employeeId]);
    }
  };

  const handleSelectAllCheckboxChange = (e) => {
    if (e.target.checked) {
      setSelectedPensionerIds(pensioners.map(p => p.employee_id));
    } else {
      setSelectedPensionerIds([]);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
      <div className="flex items-center gap-3">
        <Landmark className="w-8 h-8 text-primary-600" />
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Pension Payroll Management</h1>
        </div>
      </div>

      {/* Ribbon format Tab Buttons */}
      <div className="flex gap-4 p-2 bg-gray-50 border border-gray-200 rounded-xl">
        {canViewGenTab && (
          <button
            onClick={() => { setActiveRibbonTab('generation'); setSelectedRun(null); }}
            className={`flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-lg font-semibold text-sm transition-all ${activeRibbonTab === 'generation' ? 'bg-primary-600 text-white shadow-md' : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
              }`}
          >
            <Calendar className="w-4 h-4" /> Payroll Generation
          </button>
        )}
        {canViewHistTab && (
          <button
            onClick={() => { setActiveRibbonTab('historical'); setSelectedRun(null); }}
            className={`flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-lg font-semibold text-sm transition-all ${activeRibbonTab === 'historical' ? 'bg-primary-600 text-white shadow-md' : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
              }`}
          >
            <Upload className="w-4 h-4" /> Historical Pension Payroll Import
          </button>
        )}
        {canViewMasterTab && (
          <button
            onClick={() => { setActiveRibbonTab('master'); setSelectedRun(null); }}
            className={`flex-1 flex-items-center justify-center gap-2 py-3 px-4 rounded-lg font-semibold text-sm transition-all ${activeRibbonTab === 'master' ? 'bg-primary-600 text-white shadow-md' : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
              }`}
          >
            <Play className="w-4 h-4" /> Pensioner Payroll Master
          </button>
        )}
      </div>

      {/* 1. Payroll Generation view */}
      {activeRibbonTab === 'generation' && !selectedRun && canViewGenTab && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-6 h-fit">
            <h2 className="text-xl font-bold text-gray-800 mb-2">Process New Run</h2>
            <p className="text-xs text-gray-500 mb-6">Select month & year to run pension payroll</p>
            <form onSubmit={handleGenerate} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Month</label>
                  <select
                    value={genMonth}
                    onChange={(e) => setGenMonth(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none bg-white"
                  >
                    {genMonthOptions.map((mNum) => (
                      <option key={mNum} value={mNum}>
                        {new Date(2025, mNum - 1).toLocaleString('default', { month: 'long' })}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Year</label>
                  <select
                    value={genYear}
                    onChange={handleGenYearChange}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none bg-white"
                  >
                    {genYearOptions.map((yVal) => (
                      <option key={yVal} value={yVal}>
                        {yVal}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Notes</label>
                <textarea
                  value={genDescription}
                  onChange={(e) => setGenDescription(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none"
                  rows="3"
                ></textarea>
              </div>
              <button
                type="submit"
                disabled={generating}
                className="w-full py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg text-sm font-semibold transition-colors"
              >
                {generating ? 'Processing...' : 'Generate Run'}
              </button>
            </form>
          </div>

          <div className="lg:col-span-2 bg-white rounded-xl shadow-lg border border-gray-100 p-6">
            <h2 className="text-xl font-bold text-gray-800 mb-4">Generated Pension Payroll List</h2>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50 text-gray-700 text-xs font-semibold uppercase">
                  <tr>
                    <th className="px-4 py-3 text-left">Period</th>
                    <th className="px-4 py-3 text-left">Processed Date</th>
                    <th className="px-4 py-3 text-left">Total Pensioners</th>
                    <th className="px-4 py-3 text-left">Net Amount</th>
                    <th className="px-4 py-3 text-left">Status</th>
                    <th className="px-4 py-3 text-center">Action</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200 text-sm text-gray-700">
                  {runs.map((r) => (
                    <tr key={r.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 font-semibold">
                        {new Date(2025, r.payroll_month - 1).toLocaleString('default', { month: 'long' })} {r.payroll_year}
                      </td>
                      <td className="px-4 py-3 text-xs">{r.run_date}</td>
                      <td className="px-4 py-3">{r.total_pensioners}</td>
                      <td className="px-4 py-3 font-bold">₹{parseFloat(r.total_net_pension).toFixed(2)}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${r.status === 'approved' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                          }`}>
                          {r.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <div className="flex justify-center items-center gap-3">
                          <button
                            onClick={() => handleSelectRun(r.id)}
                            className="text-xs font-semibold text-primary-600 hover:text-primary-800"
                          >
                            View Details
                          </button>
                          <button
                            onClick={() => handleOpenSummaryReport(r)}
                            className="text-xs font-semibold text-indigo-600 hover:text-indigo-800 flex items-center gap-1"
                          >
                            <FileText className="w-3.5 h-3.5" /> Paybill Summary
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {activeRibbonTab === 'generation' && selectedRun && !viewPayslipEmployeeId && canViewGenTab && (
        <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-6 space-y-6">
          <div className="flex items-center justify-between pb-4 border-b border-gray-150">
            <div>
              <button onClick={() => setSelectedRun(null)} className="text-xs text-primary-600 hover:text-primary-800 font-semibold mb-1 block">
                ← Back to Runs List
              </button>
              <h2 className="text-xl font-bold text-gray-800">
                Run: {new Date(2025, selectedRun.payroll_month - 1).toLocaleString('default', { month: 'long' })} {selectedRun.payroll_year}
              </h2>
            </div>
            <div className="flex items-center gap-3">
              {/* Draft run excel re-upload update option */}
              {selectedRun.status === 'draft' && (
                <form onSubmit={handleImportDraftUpdate} className="flex items-center gap-2 border-r border-gray-200 pr-3">
                  <input
                    type="file"
                    accept=".xlsx"
                    onChange={(e) => setDraftUpdateFile(e.target.files[0])}
                    className="text-xs max-w-xs focus:outline-none file:mr-2 file:py-1 file:px-2 file:rounded file:border-0 file:text-xs file:bg-primary-50 file:text-primary-700 hover:file:bg-primary-100"
                  />
                  <button
                    type="submit"
                    disabled={updatingDraft || !draftUpdateFile}
                    className={`px-3 py-1.5 rounded-lg text-xs font-semibold ${!draftUpdateFile ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-primary-600 text-white'
                      }`}
                  >
                    {updatingDraft ? 'Updating...' : 'Import Excel Update'}
                  </button>
                </form>
              )}
              {canExport && (
                <>
                  <button onClick={() => handleExportRun(selectedRun.id)} className="px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-800 rounded-lg text-xs font-semibold flex items-center gap-1.5 transition-colors">
                    <Download className="w-3.5 h-3.5" /> Export CSV
                  </button>
                  <button
                    onClick={() => handlePreviewPdf(selectedRun.id)}
                    disabled={pdfLoading}
                    className="px-3 py-1.5 bg-blue-50 hover:bg-blue-100 text-blue-700 rounded-lg text-xs font-semibold flex items-center gap-1.5 transition-colors"
                  >
                    <Eye className="w-3.5 h-3.5" /> {pdfLoading ? 'Loading PDF...' : 'Preview PDF'}
                  </button>
                  <button
                    onClick={() => handleDownloadPdf(selectedRun)}
                    className="px-3 py-1.5 bg-primary-600 hover:bg-primary-700 text-white rounded-lg text-xs font-semibold flex items-center gap-1.5 transition-colors"
                  >
                    <Download className="w-3.5 h-3.5" /> Download PDF
                  </button>
                  <button
                    onClick={() => handleOpenSummaryReport(selectedRun)}
                    className="px-3 py-1.5 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 rounded-lg text-xs font-semibold flex items-center gap-1.5 transition-colors"
                  >
                    <FileText className="w-3.5 h-3.5" /> Paybill Summary
                  </button>
                </>
              )}
              {canApprove && selectedRun.status === 'draft' && (
                <button onClick={() => handleApproveRun(selectedRun.id)} className="px-3 py-1.5 bg-green-600 text-white rounded-lg text-xs font-semibold">
                  Approve & Finalize
                </button>
              )}
              {isAdmin && selectedRun.status === 'approved' && (
                <button onClick={() => handleRejectRun(selectedRun.id)} className="px-3 py-1.5 bg-rose-600 hover:bg-rose-700 text-white rounded-lg text-xs font-semibold">
                  Reject & Revert to Draft
                </button>
              )}
              {selectedRun.status === 'draft' && (
                <button
                  onClick={() => handleDeleteRun(selectedRun.id)}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-red-50 hover:bg-red-100 text-red-700 rounded-lg text-xs font-semibold transition-colors"
                >
                  <Trash2 className="w-3.5 h-3.5" /> Delete Draft
                </button>
              )}
            </div>
          </div>

          <div className="grid grid-cols-5 gap-4 text-center text-sm">
            <div className="bg-gray-50 p-4 rounded-lg">
              <p className="text-gray-500 font-medium">Pensioners Count</p>
              <p className="text-lg font-bold text-gray-800 mt-1">{selectedRun.total_pensioners}</p>
            </div>
            <div className="bg-gray-50 p-4 rounded-lg">
              <p className="text-gray-500 font-medium">Total Basic</p>
              <p className="text-lg font-bold text-gray-800 mt-1">₹{Math.round(parseFloat(selectedRun.total_basic || 0))}</p>
            </div>
            <div className="bg-gray-50 p-4 rounded-lg">
              <p className="text-gray-500 font-medium">Total Medical</p>
              <p className="text-lg font-bold text-gray-800 mt-1">₹{Math.round(parseFloat(selectedRun.total_medical || 0))}</p>
            </div>
            <div className="bg-gray-50 p-4 rounded-lg">
              <p className="text-gray-500 font-medium">Total IT Deducted</p>
              <p className="text-lg font-bold text-rose-700 mt-1">₹{Math.round(parseFloat(selectedRun.total_income_tax || 0))}</p>
            </div>
            <div className="bg-gray-50 p-4 rounded-lg">
              <p className="text-gray-500 font-medium">Net Disbursed</p>
              <p className="text-lg font-bold text-primary-800 mt-1">₹{Math.round(parseFloat(selectedRun.total_net_pension || 0))}</p>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50 text-gray-700 text-xs font-semibold uppercase">
                <tr>
                  <th className="px-2 py-3 text-left">Sr. No.</th>
                  <th className="px-2 py-3 text-left">Pens. Code</th>
                  <th className="px-2 py-3 text-left min-w-[120px]">Pensioner Name</th>
                  <th className="px-2 py-3 text-left min-w-[120px]">Family Pensioner Name</th>
                  <th className="px-2 py-3 text-left">Designation</th>
                  <th className="px-2 py-3 text-left min-w-[100px]">Date of Ret.</th>
                  <th className="px-2 py-3 text-left min-w-[100px]">Date of Death</th>
                  <th className="px-2 py-3 text-left min-w-[100px]">Pensioner Type</th>
                  <th className="px-2 py-3 text-left">Basic Amnt</th>
                  <th className="px-2 py-3 text-left">Dear. Allow.</th>
                  <th className="px-2 py-3 text-left">D.A. Arrear</th>
                  <th className="px-2 py-3 text-left">Medical Allow.</th>
                  <th className="px-2 py-3 text-left">Gross Total</th>
                  <th className="px-2 py-3 text-left">Comm. Amnt.</th>
                  <th className="px-2 py-3 text-left">Recovery</th>
                  <th className="px-2 py-3 text-left">Income Tax</th>
                  <th className="px-2 py-3 text-left">Total</th>
                  <th className="px-2 py-3 text-center">Action</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200 text-sm text-gray-700">
                {records.map((r, index) => {
                  const isEditingDraft = editingRecordId === r.id;
                  return (
                    <tr key={r.id} className="hover:bg-gray-50">
                      <td className="px-2 py-3 font-medium">{index + 1}</td>
                      <td className="px-2 py-3 font-medium">{r.employee_code}</td>
                      <td className="px-2 py-3 font-medium">{r.employee_name}</td>
                      <td className="px-2 py-3 font-semibold text-blue-900">{r.relation ? r.recipient_name : '-'}</td>
                      <td className="px-2 py-3 text-xs text-gray-500">{r.designation}</td>
                      <td className="px-2 py-3 text-xs">{r.date_of_retirement || '-'}</td>
                      <td className="px-2 py-3 text-xs">{r.date_of_death || '0000-00-00'}</td>
                      <td className="px-2 py-3 text-xs">
                        <span className={`inline-flex px-1.5 py-0.5 rounded text-[10px] font-bold ${r.relation ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}`}>
                          {r.relation || 'SELF'}
                        </span>
                      </td>
                      <td className="px-2 py-3">
                        {isEditingDraft ? (
                          <input type="number" value={editBasic} onChange={(e) => setEditBasic(e.target.value)} className="w-16 border border-gray-300 rounded px-1.5 py-0.5 text-xs focus:outline-none" />
                        ) : (`₹${Math.round(parseFloat(r.basic_pension || 0))}`)}
                      </td>
                      <td className="px-2 py-3 text-gray-500">₹{Math.round(parseFloat(r.da_amount || 0))}</td>
                      <td className="px-2 py-3">
                        {isEditingDraft ? (
                          <input type="number" value={editDaArrear} onChange={(e) => setEditDaArrear(e.target.value)} className="w-16 border border-gray-300 rounded px-1.5 py-0.5 text-xs focus:outline-none" />
                        ) : (`₹${Math.round(parseFloat(r.da_arrear || 0))}`)}
                      </td>
                      <td className="px-2 py-3">
                        {isEditingDraft ? (
                          <input type="number" value={editMedical} onChange={(e) => setEditMedical(e.target.value)} className="w-16 border border-gray-300 rounded px-1.5 py-0.5 text-xs focus:outline-none" />
                        ) : (`₹${Math.round(parseFloat(r.medical_allowance || 0))}`)}
                      </td>
                      <td className="px-2 py-3 font-semibold">₹{Math.round(parseFloat(r.gross_pension || 0))}</td>
                      <td className="px-2 py-3">
                        {isEditingDraft ? (
                          <input type="number" value={editCommutation} onChange={(e) => setEditCommutation(e.target.value)} className="w-16 border border-gray-300 rounded px-1.5 py-0.5 text-xs focus:outline-none" />
                        ) : (`₹${Math.round(parseFloat(r.commutation_amount || 0))}`)}
                      </td>
                      <td className="px-2 py-3">
                        {isEditingDraft ? (
                          <input type="number" value={editRecovery} onChange={(e) => setEditRecovery(e.target.value)} className="w-16 border border-gray-300 rounded px-1.5 py-0.5 text-xs focus:outline-none" />
                        ) : (`₹${Math.round(parseFloat(r.recovery || 0))}`)}
                      </td>
                      <td className="px-2 py-3">
                        {isEditingDraft ? (
                          <input type="number" value={editIncomeTax} onChange={(e) => setEditIncomeTax(e.target.value)} className="w-16 border border-gray-300 rounded px-1.5 py-0.5 text-xs focus:outline-none" />
                        ) : (`₹${Math.round(parseFloat(r.income_tax || 0))}`)}
                      </td>
                      <td className="px-2 py-3 font-semibold text-primary-900">₹{Math.round(parseFloat(r.net_pension || 0))}</td>
                      <td className="px-2 py-3 text-center">
                        <div className="flex justify-center items-center gap-2">
                          {selectedRun.status === 'draft' ? (
                            isEditingDraft ? (
                              <>
                                <button onClick={() => saveEditDraftRecord(selectedRun.id, r.id)} className="text-green-600 hover:text-green-800 p-0.5">
                                  <Save className="w-3.5 h-3.5" />
                                </button>
                                <button onClick={() => setEditingRecordId(null)} className="text-red-600 hover:text-red-800 p-0.5">
                                  <X className="w-3.5 h-3.5" />
                                </button>
                              </>
                            ) : (
                              <button onClick={() => startEditDraftRecord(r)} className="text-blue-600 hover:text-blue-800 p-0.5">
                                <Edit2 className="w-3.5 h-3.5" />
                              </button>
                            )
                          ) : null}
                          <button onClick={() => setViewPayslipEmployeeId(r.employee_id)} className="text-primary-600 hover:text-primary-800 text-xs font-semibold ml-2">
                            View Slip
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeRibbonTab === 'generation' && selectedRun && viewPayslipEmployeeId && canViewGenTab && (
        <PensionPayslipDetail
          runId={selectedRun.id}
          employeeId={viewPayslipEmployeeId}
          onBack={() => setViewPayslipEmployeeId(null)}
        />
      )}

      {/* 2. Historical Pension Payroll Import view */}
      {activeRibbonTab === 'historical' && canViewHistTab && (
        <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-6 max-w-xl mx-auto space-y-6">
          <div>
            <h2 className="text-xl font-bold text-gray-800">Historical Pension Payroll Import</h2>
            <p className="text-xs text-gray-500 mt-1">Upload and import past pension statements using excel files</p>
          </div>

          <form onSubmit={handleHistoricalImport} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Month</label>
                <select
                  value={histMonth}
                  onChange={(e) => setHistMonth(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none bg-white"
                >
                  {histMonthOptions.map((mNum) => (
                    <option key={mNum} value={mNum}>
                      {new Date(2025, mNum - 1).toLocaleString('default', { month: 'long' })}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Year</label>
                <select
                  value={histYear}
                  onChange={handleHistYearChange}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none bg-white"
                >
                  {histYearOptions.map((yVal) => (
                    <option key={yVal} value={yVal}>
                      {yVal}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">Select Excel File (.xlsx)</label>
              <input
                type="file"
                accept=".xlsx"
                onChange={(e) => setHistFile(e.target.files[0])}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none file:mr-4 file:py-1 file:px-2 file:rounded-md file:border-0 file:text-xs file:bg-primary-50 file:text-primary-700 file:font-semibold hover:file:bg-primary-100"
              />
            </div>

            <button
              type="submit"
              disabled={importing}
              className="w-full flex items-center justify-center gap-2 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg text-sm font-semibold transition-colors"
            >
              {importing ? 'Importing...' : (
                <>
                  <Upload className="w-4 h-4" /> Validate & Import Pension Run
                </>
              )}
            </button>
          </form>
        </div>
      )}

      {/* 3. Pensioner Payroll Master view */}
      {activeRibbonTab === 'master' && canViewMasterTab && (
        <div className="space-y-6">
          {/* Global Assigner Panel */}
          {canManageDA && (
            <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-6 space-y-4">
              <h2 className="text-lg font-bold text-gray-800">Global Assigner</h2>
              <form onSubmit={handleApplyGlobal} className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">DA Percentage (%)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={globalDa}
                    onChange={(e) => setGlobalDa(e.target.value)}
                    placeholder="e.g. 53.0"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Medical Allowance (₹)</label>
                  <input
                    type="number"
                    step="100"
                    value={globalMedical}
                    onChange={(e) => setGlobalMedical(e.target.value)}
                    placeholder="e.g. 500"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Assignment Mode</label>
                  <select
                    value={globalMode}
                    onChange={(e) => setGlobalMode(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none"
                  >
                    <option value="all">Apply to All</option>
                    <option value="selected">Apply to Selected</option>
                  </select>
                </div>
                <button
                  type="submit"
                  className="py-2 px-4 bg-primary-600 hover:bg-primary-700 text-white rounded-lg text-sm font-semibold transition-colors"
                >
                  Apply Assignment Settings
                </button>
              </form>
            </div>
          )}

          {/* Pensioner master database list table */}
          <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-bold text-gray-800">Pensioner Master Accounts List</h2>
              {selectedPensionerIds.length > 0 && (
                <span className="text-xs font-medium text-primary-600 bg-primary-50 px-2.5 py-1 rounded-full">
                  {selectedPensionerIds.length} pensioners selected
                </span>
              )}
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50 text-gray-700 text-xs font-semibold uppercase">
                  <tr>
                    <th className="px-4 py-3 text-left">
                      <input
                        type="checkbox"
                        onChange={handleSelectAllCheckboxChange}
                        checked={pensioners.length > 0 && selectedPensionerIds.length === pensioners.length}
                      />
                    </th>
                    <th className="px-4 py-3 text-left">Employee Code</th>
                    <th className="px-4 py-3 text-left">Name</th>
                    <th className="px-4 py-3 text-left">Basic Pension</th>
                    <th className="px-4 py-3 text-left">DA Rate %</th>
                    <th className="px-4 py-3 text-left">DA Amount</th>
                    <th className="px-4 py-3 text-left">Medical Allowance</th>
                    <th className="px-4 py-3 text-left">Total Pension</th>
                    <th className="px-4 py-3 text-center">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200 text-sm text-gray-700">
                  {pensioners.map((p) => {
                    const isEditing = editingEmployeeId === p.employee_id;
                    return (
                      <tr key={p.employee_id} className="hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <input
                            type="checkbox"
                            checked={selectedPensionerIds.includes(p.employee_id)}
                            onChange={() => handleCheckboxChange(p.employee_id)}
                          />
                        </td>
                        <td className="px-4 py-3 font-semibold">{p.employee_code}</td>
                        <td className="px-4 py-3">{p.employee_name}</td>
                        <td className="px-4 py-3">
                          {isEditing ? (
                            <input
                              type="number"
                              value={editBasic}
                              onChange={(e) => setEditBasic(e.target.value)}
                              className="w-24 border border-gray-300 rounded px-2 py-0.5"
                            />
                          ) : (
                            `₹${parseFloat(p.basic_pension).toFixed(2)}`
                          )}
                        </td>
                        <td className="px-4 py-3">
                          {isEditing ? (
                            <input
                              type="number"
                              step="0.01"
                              value={editDaPct}
                              onChange={(e) => setEditDaPct(e.target.value)}
                              className="w-16 border border-gray-300 rounded px-2 py-0.5"
                            />
                          ) : (
                            `${p.da_percentage}%`
                          )}
                        </td>
                        <td className="px-4 py-3 text-gray-500">
                          ₹{parseFloat(p.da_amount).toFixed(2)}
                        </td>
                        <td className="px-4 py-3 font-semibold">
                          {isEditing ? (
                            <input
                              type="number"
                              value={editMedical}
                              onChange={(e) => setEditMedical(e.target.value)}
                              className="w-20 border border-gray-300 rounded px-2 py-0.5"
                            />
                          ) : (
                            `₹${parseFloat(p.medical_allowance || 0.00).toFixed(2)}`
                          )}
                        </td>
                        <td className="px-4 py-3 font-bold text-primary-900">
                          ₹{parseFloat(p.total_pension).toFixed(2)}
                        </td>
                        <td className="px-4 py-3 text-center">
                          {isEditing ? (
                            <div className="flex justify-center gap-2">
                              <button onClick={() => saveEditMaster(p.employee_id)} className="text-green-600 hover:text-green-800 p-1">
                                <Save className="w-4 h-4" />
                              </button>
                              <button onClick={() => setEditingEmployeeId(null)} className="text-red-600 hover:text-red-800 p-1">
                                <X className="w-4 h-4" />
                              </button>
                            </div>
                          ) : (
                            <button onClick={() => startEditMaster(p)} className="text-primary-600 hover:text-primary-850 p-1">
                              <Edit2 className="w-4 h-4" />
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                  {pensioners.length === 0 && (
                    <tr>
                      <td colSpan="9" className="px-4 py-8 text-center text-gray-500">No pensioners found.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
      {/* PDF Preview Modal */}
      {showPdfModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-5xl h-[85vh] flex flex-col overflow-hidden">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gray-50">
              <div className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-primary-600" />
                <h3 className="font-bold text-gray-800 text-base">
                  PDF Preview — Pension Payroll Statement ({selectedRun ? `${new Date(2025, selectedRun.payroll_month - 1).toLocaleString('default', { month: 'long' })} ${selectedRun.payroll_year}` : ''})
                </h3>
              </div>
              <div className="flex items-center gap-3">
                {selectedRun && (
                  <button
                    onClick={() => handleDownloadPdf(selectedRun)}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-primary-600 hover:bg-primary-700 text-white rounded-lg text-xs font-semibold transition-colors"
                  >
                    <Download className="w-3.5 h-3.5" /> Download PDF
                  </button>
                )}
                <button
                  onClick={() => {
                    setShowPdfModal(false);
                    if (pdfPreviewUrl) {
                      URL.revokeObjectURL(pdfPreviewUrl);
                      setPdfPreviewUrl(null);
                    }
                  }}
                  className="p-1 text-gray-400 hover:text-gray-600 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            <div className="flex-1 bg-gray-200 p-2 overflow-hidden">
              {pdfPreviewUrl ? (
                <iframe
                  src={`${pdfPreviewUrl}#toolbar=0`}
                  title="Pension Payroll Statement PDF Preview"
                  className="w-full h-full rounded border-0 shadow"
                />
              ) : (
                <div className="flex items-center justify-center h-full text-gray-500 text-sm">
                  Loading PDF preview...
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      {/* Pension Paybill Summary / Synopsis Statement Modal */}
      {summaryModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gray-50">
              <div className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-indigo-600" />
                <h3 className="font-bold text-gray-800 text-base">
                  Pension Paybill Summary Statement
                </h3>
              </div>
              <div className="flex items-center gap-3">
                {selectedSummaryRun && (
                  <button
                    onClick={() => handleDownloadSummaryPdf(selectedSummaryRun)}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-xs font-semibold transition-colors shadow-sm"
                  >
                    <Download className="w-3.5 h-3.5" /> Download Summary PDF
                  </button>
                )}
                <button
                  onClick={() => {
                    setSummaryModalOpen(false);
                    setSummaryData(null);
                  }}
                  className="p-1 text-gray-400 hover:text-gray-600 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            <div className="flex-1 p-6 overflow-y-auto bg-gray-100 flex justify-center">
              <div className="bg-white p-8 rounded shadow-sm border border-gray-300 w-full max-w-xl text-gray-800 font-sans">
                {summaryLoading ? (
                  <div className="text-center py-12">
                    <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                    <p className="mt-4 text-xs text-gray-500 font-medium">Loading Paybill Summary...</p>
                  </div>
                ) : summaryData ? (
                  <div className="space-y-6">
                    {/* Document Header */}
                    <div className="flex items-start justify-between border-b pb-4 border-gray-200">
                      <div>
                        <h2 className="text-lg font-extrabold text-blue-950 uppercase tracking-tight">Pune Cantonment Board</h2>
                        <p className="text-xs font-bold text-gray-600 mt-0.5 uppercase">Pension Paybill Summary Statement — {summaryData.month_name} {summaryData.payroll_year}</p>
                      </div>
                      <div className="text-right">
                        <span className="text-xs font-bold text-gray-700 uppercase tracking-wider block">DATE - {summaryData.run_date}</span>
                      </div>
                    </div>

                    {/* Section Title */}
                    <div className="text-center pt-2">
                      <h3 className="text-base font-extrabold text-gray-900 uppercase underline decoration-2 underline-offset-4 tracking-wider">GRAND TOTAL</h3>
                    </div>

                    {/* Key-Value Summary Table */}
                    <div className="py-2">
                      <table className="w-full text-sm font-semibold border-collapse">
                        <tbody>
                          <tr className="border-b border-gray-100">
                            <td className="py-2 text-left text-gray-800 font-bold uppercase">BASIC</td>
                            <td className="py-2 text-right font-mono text-gray-900">{summaryData.basic}</td>
                          </tr>
                          <tr className="border-b border-gray-100">
                            <td className="py-2 text-left text-gray-800 font-bold uppercase">DEAR. ALLOW.</td>
                            <td className="py-2 text-right font-mono text-gray-900">{summaryData.dear_allow}</td>
                          </tr>
                          <tr className="border-b border-gray-100">
                            <td className="py-2 text-left text-gray-800 font-bold uppercase">DA ARRIERS</td>
                            <td className="py-2 text-right font-mono text-gray-900">{summaryData.da_arriers}</td>
                          </tr>
                          <tr className="border-b border-gray-100">
                            <td className="py-2 text-left text-gray-800 font-bold uppercase">MEDICAL ALLOW.</td>
                            <td className="py-2 text-right font-mono text-gray-900">{summaryData.medical_allow}</td>
                          </tr>
                          <tr className="border-b border-gray-200 bg-gray-50/50">
                            <td className="py-2.5 text-left text-gray-900 font-extrabold uppercase">GROSS TOTAL</td>
                            <td className="py-2.5 text-right font-mono font-extrabold text-gray-900">{summaryData.gross_total}</td>
                          </tr>
                          <tr className="border-b border-gray-100">
                            <td className="py-2 text-left text-gray-800 font-bold uppercase">COMM. AMOUNT</td>
                            <td className="py-2 text-right font-mono text-gray-900">{summaryData.comm_amount}</td>
                          </tr>
                          <tr className="border-b border-gray-100">
                            <td className="py-2 text-left text-gray-800 font-bold uppercase">RECOVERY</td>
                            <td className="py-2 text-right font-mono text-gray-900">{summaryData.recovery}</td>
                          </tr>
                          <tr className="border-b border-gray-800">
                            <td className="py-2 text-left text-gray-800 font-bold uppercase">INCOME TAX</td>
                            <td className="py-2 text-right font-mono text-gray-900">{summaryData.income_tax}</td>
                          </tr>
                          {/* Final TOTAL Row */}
                          <tr className="border-b-2 border-t-2 border-gray-900 bg-amber-50/50">
                            <td className="py-3 text-left text-gray-950 font-black text-base uppercase">TOTAL</td>
                            <td className="py-3 text-right font-mono font-black text-base text-gray-950">{summaryData.total}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>

                    {/* Total in Words */}
                    <div className="pt-3 pb-4 text-xs font-bold text-gray-900 border-b border-gray-200 leading-relaxed uppercase">
                      {summaryData.total_in_words}
                    </div>

                    {/* Signatures Section */}
                    <div className="pt-12 pb-4 grid grid-cols-4 gap-2 text-center text-[10px] font-bold text-gray-800 uppercase tracking-tight">
                      <div>PENSION CLERK(SR)</div>
                      <div>ACCOUNTANT</div>
                      <div>CHIEF ACCOUNTANT</div>
                      <div>CHIEF EXECUTIVE OFFICER<br />PUNE CANTONMENT BOARD</div>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-12 text-xs text-red-600">
                    Failed to load summary statement data.
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PensionPayrollManagement;
